import Vue from 'vue'
import { platform, setSessionExpiredHandler } from '@/services/platform'
import {
  readSession,
  writeSession,
  buildExpiresAt,
  isSessionValid
} from '@/services/session.mjs'
import toast from './toast'

const VERIFY_CODE_COUNTDOWN = 60

const authState = Vue.observable({
  visible: false,
  session: readSession(window.localStorage),
  user: null,
  pendingAction: null,
  countdown: 0,
  timer: null,
  submitting: false
})

if (authState.session) {
  authState.user = authState.session.user
}

export function isLoggedIn() {
  return Boolean(
    authState.session &&
    authState.user &&
    isSessionValid(authState.session)
  )
}

// 方法：清理登录态（token 与用户信息）并打开登录弹窗。
// 由 platform 的 401 刷新失败路径触发，页面无需感知底层细节。
function handleSessionExpired() {
  authState.session = null
  authState.user = null
  writeSession(window.localStorage, null)
  toast.show('登录已过期，请重新登录')
  openLoginDialog()
}

// 关键节点：注册会话过期处理器，刷新失败时自动清理并打开登录弹窗。
setSessionExpiredHandler(handleSessionExpired)

export function openLoginDialog(pendingAction) {
  authState.pendingAction =
    typeof pendingAction === 'function' ? pendingAction : null
  authState.visible = true
}

export function closeLoginDialog() {
  authState.visible = false
  authState.pendingAction = null
}

export function requireLogin(action) {
  if (isLoggedIn()) {
    if (typeof action === 'function') {
      action()
    }
    return
  }
  openLoginDialog(action)
}

export function startVerifyCodeCountdown() {
  if (authState.countdown > 0) {
    return
  }
  authState.countdown = VERIFY_CODE_COUNTDOWN
  authState.timer = window.setInterval(() => {
    authState.countdown -= 1
    if (authState.countdown <= 0) {
      window.clearInterval(authState.timer)
      authState.timer = null
      authState.countdown = 0
    }
  }, 1000)
}

export async function sendSmsCode(mobile) {
  await platform.sendSms(mobile)
  startVerifyCodeCountdown()
}

export async function completeLogin(mobile, code) {
  authState.submitting = true
  try {
    const tokens = await platform.verifySms(mobile, code)
    const sessionWithoutUser = {
      accessToken: tokens.access_token,
      refreshToken: tokens.refresh_token,
      expiresIn: tokens.expires_in,
      expiresAt: buildExpiresAt(tokens.expires_in),
      user: null
    }
    writeSession(window.localStorage, sessionWithoutUser)
    authState.session = sessionWithoutUser
    const platformUser = await platform.me()
    const user = {
      id: platformUser.id,
      mobile,
      maskedMobile: platformUser.phone,
      status: platformUser.status
    }
    const session = { ...sessionWithoutUser, user }
    authState.session = session
    authState.user = user
    writeSession(window.localStorage, session)
    authState.visible = false

    const nextAction = authState.pendingAction
    authState.pendingAction = null
    if (typeof nextAction === 'function') {
      await nextAction()
      return true
    }
    return false
  } catch (error) {
    authState.session = null
    authState.user = null
    writeSession(window.localStorage, null)
    throw error
  } finally {
    authState.submitting = false
  }
}

export async function logout() {
  const refreshToken =
    authState.session && authState.session.refreshToken

  try {
    // 逻辑分支：存在 refresh token 时先通知服务端注销当前会话
    if (refreshToken) {
      await platform.logout(refreshToken)
    }
  } finally {
    // 关键节点：退出接口完成或异常后，统一清空本地登录信息（token、user、过期时间）
    authState.session = null
    authState.user = null
    writeSession(window.localStorage, null)
  }
}

export default {
  state: authState,
  isLoggedIn,
  openLoginDialog,
  closeLoginDialog,
  requireLogin,
  startVerifyCodeCountdown,
  sendSmsCode,
  completeLogin,
  logout
}
